/**
 * Shared Zod schemas for the fleet mutating endpoints.
 *
 * Powers the VehicleType catalog routes and the VehicleFeature catalog
 * routes. The matching admin dialogs (VehicleTypeDialog, FeatureDialog)
 * keep their existing local-state pattern — the schemas here exist so the
 * server uses them via `parseBody` and the SPA can run them client-side
 * before calling its `onSave` prop, giving the same error shape on both
 * sides.
 */
import { z } from 'zod';

/**
 * Allowed shape for a VehicleType `key`: starts with an uppercase letter,
 * then uppercase letters / digits / underscores. Matches the seeded keys
 * (GENERAL, CLINIC, BUS) and the spec §6.6 rule "key is uppercase, unique".
 */
const VEHICLE_TYPE_KEY_RE = /^[A-Z][A-Z0-9_]*$/;

/** Body schema for `POST /api/vehicle-types`. Creates a non-system row. */
export const vehicleTypeCreateSchema = z.object({
  key: z
    .string()
    .trim()
    .min(1, { message: 'fleetDb.errors.vehicleTypeKeyRequired' })
    .regex(VEHICLE_TYPE_KEY_RE, { message: 'fleetDb.errors.vehicleTypeKeyInvalid' }),
  label: z.string().trim().min(1, { message: 'fleetDb.errors.vehicleTypeLabelRequired' }),
  description: z.string().trim().optional(),
  sortOrder: z.number().int().min(0).max(9999).optional(),
});

export type VehicleTypeCreateInput = z.infer<typeof vehicleTypeCreateSchema>;

/**
 * Body schema for `PATCH /api/vehicle-types/:id`. All fields optional — only
 * the supplied keys are written. System-row protection (key immutability)
 * is enforced in the route, not the schema, because it depends on DB state.
 */
export const vehicleTypeUpdateSchema = z.object({
  key: z
    .string()
    .trim()
    .min(1, { message: 'fleetDb.errors.vehicleTypeKeyRequired' })
    .regex(VEHICLE_TYPE_KEY_RE, { message: 'fleetDb.errors.vehicleTypeKeyInvalid' })
    .optional(),
  label: z
    .string()
    .trim()
    .min(1, { message: 'fleetDb.errors.vehicleTypeLabelRequired' })
    .optional(),
  description: z.string().trim().optional(),
  sortOrder: z.number().int().min(0).max(9999).optional(),
});

export type VehicleTypeUpdateInput = z.infer<typeof vehicleTypeUpdateSchema>;

/** Body schema for `POST /api/fleet/features`. */
export const vehicleFeatureCreateSchema = z.object({
  nameDe: z.string().trim().min(1, { message: 'fleetDb.errors.featureNameDeRequired' }),
  nameEn: z.string().trim().min(1, { message: 'fleetDb.errors.featureNameEnRequired' }),
  category: z.string().trim().min(1, { message: 'fleetDb.errors.featureCategoryRequired' }),
  visibleInDispatch: z.boolean().optional(),
  description: z.string().trim().optional(),
});

export type VehicleFeatureCreateInput = z.infer<typeof vehicleFeatureCreateSchema>;

/** Body schema for `PUT /api/fleet/features/:id`. All fields optional. */
export const vehicleFeatureUpdateSchema = z.object({
  nameDe: z
    .string()
    .trim()
    .min(1, { message: 'fleetDb.errors.featureNameDeRequired' })
    .optional(),
  nameEn: z
    .string()
    .trim()
    .min(1, { message: 'fleetDb.errors.featureNameEnRequired' })
    .optional(),
  category: z
    .string()
    .trim()
    .min(1, { message: 'fleetDb.errors.featureCategoryRequired' })
    .optional(),
  visibleInDispatch: z.boolean().optional(),
  description: z.string().trim().optional(),
});

export type VehicleFeatureUpdateInput = z.infer<typeof vehicleFeatureUpdateSchema>;

// ── FleetVehicle POST/PUT ─────────────────────────────────────────────────

/**
 * Body schema for `POST /api/fleet/vehicles`. The route still enforces
 * additional rules that depend on the catalog row's `key` (license required
 * for CLINIC, driver required for non-CLINIC/non-BUS) — those depend on a
 * DB lookup and stay in the handler.
 */
export const vehicleCreateSchema = z.object({
  plateNumber:   z.string().trim().min(1, { message: 'fleetDb.errors.vehiclePlateRequired' }),
  model:         z.string().trim().min(1, { message: 'fleetDb.errors.vehicleModelRequired' }),
  vehicleTypeId: z.string().trim().min(1, { message: 'fleetDb.errors.vehicleTypeIdRequired' }),
  // licenseNumber / year / color / notes are NULLABLE optional because the
  // admin form sends literal `null` for empty optional fields (see
  // VehiclesTab.handleSave's payload mapper:
  //   year: data.year ? Number(data.year) : null
  //   color: data.color || null
  //   …
  // and the route handler already normalizes `null ?? null` uniformly. Without
  // .nullable() here Zod rejects those null values with 400 even though the
  // matching vehicleUpdateSchema below has always accepted them — which is
  // why edits worked but creates failed.
  licenseNumber: z.string().trim().nullable().optional(),
  year:          z.number().int().min(1900).max(2100).nullable().optional(),
  color:         z.string().trim().nullable().optional(),
  seats:         z.number().int().positive().optional(),
  status:        z.string().optional(),
  notes:         z.string().nullable().optional(),
  driverId:      z.string().nullable().optional(),
  featureIds:    z.array(z.string()).optional(),
});

export type VehicleCreateInput = z.infer<typeof vehicleCreateSchema>;

/**
 * Body schema for `PUT /api/fleet/vehicles/:id`. All fields optional —
 * sparse update.
 */
export const vehicleUpdateSchema = z.object({
  plateNumber:   z.string().trim().min(1).optional(),
  model:         z.string().trim().min(1).optional(),
  vehicleTypeId: z.string().trim().min(1).optional(),
  licenseNumber: z.string().trim().nullable().optional(),
  year:          z.number().int().min(1900).max(2100).nullable().optional(),
  color:         z.string().trim().nullable().optional(),
  seats:         z.number().int().positive().optional(),
  status:        z.string().optional(),
  notes:         z.string().nullable().optional(),
  driverId:      z.string().nullable().optional(),
  featureIds:    z.array(z.string()).optional(),
});

export type VehicleUpdateInput = z.infer<typeof vehicleUpdateSchema>;
